import {
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  Text,
} from "@prismicio/editor-ui"
import { useEffect, useState } from "react"
import { useBlocker } from "react-router-dom"

import type { useCreateButton } from "./CreateButton"
import type { PendingAction } from "./HeaderButtons"
import { useSaveButton } from "./SaveButton"

interface BlockNavigationOnUnsavedChangesPromptProps {
  /**
   * Whether the prompt should be shown.
   * The prompt will be shown if the user navigates away from the current page.
   */
  enabled: boolean
  action: typeof useSaveButton | typeof useCreateButton
}

/**
 * @private
 * Custom component that will block navigation on the current page
 * if the user has unsaved changes.
 *
 * This should only be used in the scope of the SaveButton component.
 */
export function BlockNavigationOnUnsavedChangesPrompt(
  props: BlockNavigationOnUnsavedChangesPromptProps,
) {
  const { enabled, action } = props

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      // In vite DEV mode, full reloads happen if hot-reload failed.
      // We don't want to disrupt the dev experience with a blocking and cross-window dialog.
      if (enabled && !import.meta.env.DEV) {
        event.preventDefault()
        event.returnValue = ""
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [enabled])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      blocker.state !== "blocked" &&
      enabled &&
      currentLocation.pathname !== nextLocation.pathname,
  )

  return (
    <DiscardOrRunActionOnUnsavedChangesDialog
      action={action}
      open={blocker.state === "blocked"}
      message="You are about to leave the editor, but you have updated this document"
      onOpenChange={() => blocker.reset?.()}
      onDiscard={() => blocker.proceed?.()}
    />
  )
}

interface DiscardOrRunActionOnUnsavedChangesDialogProps {
  open: boolean
  message: string
  onOpenChange: (open: boolean) => void
  onDiscard: () => void
  onSaveSuccess?: () => void
  action: typeof useSaveButton | typeof useCreateButton
}

/**
 * This component allows us to warn the user that they are about to do an action
 * that will move them away from their current changes.
 * It will allow the user to save or discard their changes.
 *
 * Save logic is matching the behavior of the SaveButton component. This allows
 * us to handle the save in cases outside of the scope of the save button.
 */
export function DiscardOrRunActionOnUnsavedChangesDialog(
  props: DiscardOrRunActionOnUnsavedChangesDialogProps,
) {
  const { open, message, onOpenChange, onDiscard, onSaveSuccess, action } =
    props

  const [pendingAction, setPendingAction] = useState<PendingAction>()

  const actionState = action({
    pendingAction: pendingAction,
    onPendingActionChange: (_pendingAction) => {
      if (_pendingAction === undefined) onSaveSuccess?.()
      setPendingAction(_pendingAction)
    },
  })

  return (
    <Dialog onOpenChange={onOpenChange} open={open} size="small">
      <DialogHeader icon="alert" title="You have updated this document" />

      <DialogDescription asChild>
        <DialogContent padding={16}>
          <Text>{message}</Text>
        </DialogContent>
      </DialogDescription>

      <DialogActions>
        <DialogCancelButton onClick={onDiscard}>Discard</DialogCancelButton>
        <DialogActionButton
          onClick={() => actionState.action()}
          loading={pendingAction === "save" || pendingAction === "create"}
        >
          Save to a new version
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}
